import React, { useState, useEffect } from 'react';
import { Button, Offcanvas, Form, Row, Col, InputGroup } from 'react-bootstrap';
import { FiSliders } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';

const SearchFilters = ({ onFilterChange, initialFilters = {} }) => {
  const { t } = useTranslation();
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    city: '',
    country: '',
    min_price: '',
    max_price: '',
    bedrooms: '',
    guests: '',
    property_type: '',
    ...initialFilters
  });

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      onFilterChange(filters);
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [filters]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleReset = () => {
    const resetFilters = {
      city: '',
      country: '',
      min_price: '',
      max_price: '',
      bedrooms: '',
      guests: '',
      property_type: ''
    };
    setFilters(resetFilters);
    onFilterChange(resetFilters);
  };

  return (
    <>
      <Button
        variant="outline-primary"
        onClick={() => setShowFilters(true)}
        className="d-flex align-items-center gap-2"
      >
        <FiSliders />
        <span>{t('filters.filters')}</span>
      </Button>

      <Offcanvas
        show={showFilters}
        onHide={() => setShowFilters(false)}
        placement="end"
        style={{ width: '400px', maxWidth: '90vw' }}
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title className="fw-bold">{t('filters.filters')}</Offcanvas.Title>
        </Offcanvas.Header>

        <Offcanvas.Body>
          <Form>
            {/* Location */}
            <Form.Group className="mb-4">
              <Form.Label className="fw-semibold">{t('filters.location')}</Form.Label>
              <Form.Control
                type="text"
                name="city"
                placeholder={t('filters.city')}
                value={filters.city}
                onChange={handleChange}
                className="mb-2"
              />
              <Form.Control
                type="text"
                name="country"
                placeholder={t('filters.country')}
                value={filters.country}
                onChange={handleChange}
              />
            </Form.Group>

            {/* Price Range */}
            <Form.Group className="mb-4">
              <Form.Label className="fw-semibold">{t('filters.priceRange')}</Form.Label>
              <Row>
                <Col>
                  <InputGroup>
                    <InputGroup.Text>$</InputGroup.Text>
                    <Form.Control
                      type="number"
                      name="min_price"
                      placeholder={t('filters.min')}
                      value={filters.min_price}
                      onChange={handleChange}
                    />
                  </InputGroup>
                </Col>
                <Col xs="auto" className="d-flex align-items-center">
                  <span className="text-muted">-</span>
                </Col>
                <Col>
                  <InputGroup>
                    <InputGroup.Text>$</InputGroup.Text>
                    <Form.Control
                      type="number"
                      name="max_price"
                      placeholder={t('filters.max')}
                      value={filters.max_price}
                      onChange={handleChange}
                    />
                  </InputGroup>
                </Col>
              </Row>
            </Form.Group>

            {/* Property Type */}
            <Form.Group className="mb-4">
              <Form.Label className="fw-semibold">{t('filters.propertyType')}</Form.Label>
              <Form.Select
                name="property_type"
                value={filters.property_type}
                onChange={handleChange}
              >
                <option value="">All Types</option>
                <option value="apartment">Apartment</option>
                <option value="house">House</option>
                <option value="villa">Villa</option>
                <option value="condo">Condo</option>
                <option value="cabin">Cabin</option>
                <option value="cottage">Cottage</option>
                <option value="studio">Studio</option>
                <option value="loft">Loft</option>
                <option value="townhouse">Townhouse</option>
                <option value="bungalow">Bungalow</option>
                <option value="chalet">Chalet</option>
                <option value="guesthouse">Guesthouse</option>
                <option value="hostel">Hostel</option>
                <option value="hotel">Hotel</option>
                <option value="resort">Resort</option>
              </Form.Select>
            </Form.Group>

            {/* Bedrooms */}
            <Form.Group className="mb-4">
              <Form.Label className="fw-semibold">{t('filters.bedrooms')}</Form.Label>
              <Form.Select
                name="bedrooms"
                value={filters.bedrooms}
                onChange={handleChange}
              >
                <option value="">{t('filters.any')}</option>
                <option value="1">1+</option>
                <option value="2">2+</option>
                <option value="3">3+</option>
                <option value="4">4+</option>
                <option value="5">5+</option>
              </Form.Select>
            </Form.Group>

            {/* Guests */}
            <Form.Group className="mb-4">
              <Form.Label className="fw-semibold">{t('filters.guests')}</Form.Label>
              <Form.Select
                name="guests"
                value={filters.guests}
                onChange={handleChange}
              >
                <option value="">{t('filters.any')}</option>
                <option value="1">1+</option>
                <option value="2">2+</option>
                <option value="4">4+</option>
                <option value="6">6+</option>
                <option value="8">8+</option>
              </Form.Select>
            </Form.Group>
          </Form>
        </Offcanvas.Body>

        <div className="p-3 border-top bg-light d-flex gap-2">
          <Button
            variant="outline-secondary"
            onClick={handleReset}
            className="flex-fill"
          >
            {t('filters.reset')}
          </Button>
          <Button
            variant="primary"
            onClick={() => setShowFilters(false)}
            className="flex-fill"
          >
            {t('filters.apply')}
          </Button>
        </div>
      </Offcanvas>
    </>
  );
};

export default SearchFilters;
